import { trackAddNewUserInChat, trackingUserSendMessage } from './tracking';
import { IMessageEvent, IWsFileMessage, IWsMessageEvent } from './types';
import { IOfferSchema } from '../../repositories/chats/entities/get_my_offers_for_selection/OfferSchema';
import { currentChatOpponentIdSelector, currentOpenChatSelector } from '../../selectors';
import { ERequestApiStatus, EWsClientEventType } from '../../types/appState';
import { TMessageSchema } from '../../types/messages';
import { TThunkAction } from '../../types/redux/thunk';
import { ISessionSucceedState } from '../../types/session';
import { ETelemetryEvents } from '../../types/telemetry';
import { timeoutPromise } from '../../utils/timeoutPromise';
import { EInternetStatus } from '../internetStatus';
import { setSendMessageFailed } from '../sendMessage';
import { setSocketFailed } from '../socket';

/**
 * Отправка сообщения по WS
 */

interface IProps {
  message: TMessageSchema;
  files?: IWsFileMessage[];
  offers?: IOfferSchema[];
}

export const sendWsMessage =
  ({ message, files, offers }: IProps): TThunkAction<Promise<void>> =>
  async (dispatch, getState, { logger, telemetry }) => {
    const state = getState();

    const {
      session: { status, token },
      socket: { eventChannel, clientEvents, status: socketStatus },
      internetStatus,
    } = state;

    const opponentId = currentChatOpponentIdSelector(state);
    const { users, offer } = currentOpenChatSelector(state) || {};

    const isSessionExpired = status !== ERequestApiStatus.Succeed;
    const isSocketIsClose = eventChannel.isChannelClosed() || socketStatus === ERequestApiStatus.Failed;
    const isOffline = internetStatus === EInternetStatus.Offline;

    const { chatId, messageId, text, userId } = message;

    const isMessageUserInChat = users?.find(user => user.userId === userId);

    const messageEvent: IMessageEvent = {
      chatId,
      status: ERequestApiStatus.Pending,
      type: EWsClientEventType.Message,
    };

    if (isSocketIsClose) {
      dispatch(setSocketFailed());
    }

    if (isSessionExpired || isSocketIsClose || isOffline || !chatId || chatId === 'blob') {
      clientEvents.set(messageId, { ...messageEvent, status: ERequestApiStatus.Failed });

      dispatch(setSendMessageFailed({ message, messageId }));

      return;
    }

    clientEvents.set(messageId, messageEvent);

    const wsMessageEvent: IWsMessageEvent = {
      type: EWsClientEventType.Message,
      message: {
        chatId,
        text,
        files,
        offers,
      },
      uid: messageId,
      userId,
      sessionToken: (token as ISessionSucceedState['token']).sessionToken,
    };

    try {
      await timeoutPromise(eventChannel.sendMessage(wsMessageEvent), 10000);

      trackingUserSendMessage(chatId, opponentId);

      if (!isMessageUserInChat) {
        trackAddNewUserInChat({ offer, chatId, opponentId });
      }
    } catch (err) {
      logger.error(err, {
        domain: 'frontend-chats.actions.sendWsMessage',
        description: 'Не удалось отправить сообщение по ws',
      });

      clientEvents.set(messageId, { ...messageEvent, status: ERequestApiStatus.Failed });

      dispatch(setSendMessageFailed({ message, messageId }));
      telemetry.increment(ETelemetryEvents.ChatWsMessageSendFail);
    }
  };
